import {
  DataRefreshEvent,
  type BatchProcessDto,
  type BatchProcessItemDto,
  type BatchProcessItemStatus,
  type BatchProcessItemStatusPayload,
} from "@fp/admin-api-client";
import {
  getListBatchProcessItemsQueryKey,
  listBatchProcessItems,
} from "@fp/admin-api-client/batch-processes/batch-processes.client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  advanceItemStatuses,
  observeItemStatus,
  paceBatchSummary,
  type PacedItemStatuses,
} from "./batch-process-item-pacing";
import { getBatchProcessReconcileInterval } from "./batch-process-refresh";
import { useDataRefresh } from "@/hooks/useSignalR";

const PAGE_SIZE = 25;
const TICK_MS = 100;

export interface BatchProcessRun {
  batch: BatchProcessDto | null;
  items: BatchProcessItemDto[];
  statuses: PacedItemStatuses;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

export function useBatchProcessRun(
  batch: BatchProcessDto | null,
  isEnabled: boolean,
): BatchProcessRun {
  const batchProcessId = batch?.id ?? "";
  const [statuses, setStatuses] = useState<PacedItemStatuses>({});

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: getListBatchProcessItemsQueryKey(batchProcessId, {
      pageSize: PAGE_SIZE,
    }),
    queryFn: ({ pageParam }) =>
      listBatchProcessItems(batchProcessId, {
        page: pageParam,
        pageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.data.hasNextPage ? lastPage.data.page + 1 : undefined,
    enabled: isEnabled && batchProcessId !== "",
    refetchInterval: getBatchProcessReconcileInterval(batch ? [batch] : []),
  });

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data.items ?? []) ?? [],
    [data],
  );

  const observe = (payload: BatchProcessItemStatusPayload) => {
    if (payload.batchProcessId !== batchProcessId) {
      return;
    }

    setStatuses((prev) =>
      observeItemStatus(
        prev,
        payload.batchProcessItemId,
        payload.status as BatchProcessItemStatus,
        Date.now(),
      ),
    );
  };

  useDataRefresh<BatchProcessItemStatusPayload>(
    DataRefreshEvent.BatchProcessItemStarted,
    observe,
  );
  useDataRefresh<BatchProcessItemStatusPayload>(
    DataRefreshEvent.BatchProcessItemRequeued,
    observe,
  );
  useDataRefresh<BatchProcessItemStatusPayload>(
    DataRefreshEvent.BatchProcessItemDeferred,
    observe,
  );
  useDataRefresh<BatchProcessItemStatusPayload>(
    DataRefreshEvent.BatchProcessItemCompleted,
    observe,
  );

  useEffect(() => {
    setStatuses((prev) =>
      items.reduce(
        (observed, item) =>
          observeItemStatus(observed, item.id, item.status, Date.now()),
        prev,
      ),
    );
  }, [items]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStatuses((prev) => advanceItemStatuses(prev, Date.now()));
    }, TICK_MS);

    return () => clearInterval(interval);
  }, []);

  const pacedBatch = useMemo(
    () => (batch ? paceBatchSummary(batch, items, statuses) : null),
    [batch, items, statuses],
  );

  return {
    batch: pacedBatch,
    items,
    statuses,
    isLoading,
    isError,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore: fetchNextPage,
  };
}